import React, { forwardRef, useId } from "react";
import { cx } from "components/fields/FormField";

const BOX_CLASS =
  "h-4 w-4 shrink-0 cursor-pointer rounded accent-primary focus-ring disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Native checkbox with optional inline label and description.
 *
 * Props:
 * - checked, onChange(event) (read `event.target.checked`), disabled=false.
 * - label: inline label node; description: helper text under the label (wired via aria-describedby).
 * - id: auto via useId when omitted.
 * - className: classes for the outer element (the <input> itself when there is no label/description).
 * - name, value, onClick, onBlur, … : forwarded to the <input>.
 * - ref: forwarded to the <input>.
 */
const Checkbox = forwardRef(function Checkbox(
  { id, label, description, disabled = false, className = "", ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const descriptionId = description ? `${inputId}-description` : undefined;

  if (!label && !description) {
    return (
      <input
        ref={ref}
        type="checkbox"
        id={inputId}
        disabled={disabled}
        {...rest}
        className={cx(BOX_CLASS, className)}
      />
    );
  }

  return (
    <div className={cx("flex items-start gap-2", className)}>
      <input
        ref={ref}
        type="checkbox"
        id={inputId}
        disabled={disabled}
        aria-describedby={descriptionId}
        {...rest}
        className={cx(BOX_CLASS, "mt-0.5")}
      />
      <div className="flex min-w-0 flex-col">
        {label && (
          <label
            htmlFor={inputId}
            className={cx(
              "text-sm font-medium text-fg",
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p id={descriptionId} className="text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

export default Checkbox;
